import airports from "../assets/json/airports.json";
import dayjs from "dayjs";
import ROUTE_PATHS from "./routePaths";
import {
  ADMIN_ROLE,
  COMMERCIAL_USER_ROLE,
  HISTORICAL_DATA_STORAGE_KEY,
} from "./constants";

export const RESTRICTED_ROUTE_PREFIXES_BY_ROLE = {
  [ADMIN_ROLE.OPERATION]: [
    ROUTE_PATHS.COMMERCIAL_AIRLINE_DATA_SOURCE,
    ROUTE_PATHS.COMMERCIAL_AIRLINE_CONCESSION_FEE,
    ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE,
    ROUTE_PATHS.COMMERCIAL_AIRLINE_ACCOUNT_MAINTENANCE,
  ],
  [ADMIN_ROLE.SUPER_ADMIN]: [
    ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE,
    ROUTE_PATHS.COMMERCIAL_AIRLINE_ACCOUNT_MAINTENANCE,
  ],
  [COMMERCIAL_USER_ROLE.AIRLINE_ADMIN]: [
    ROUTE_PATHS.COMMERCIAL_AIRLINE_DATA_SOURCE,
    ROUTE_PATHS.COMMERCIAL_AIRLINE_VIEW_SPECIAL_ONBOARDING,
  ],
  [COMMERCIAL_USER_ROLE.AIRLINE_AGENT]: [
    ROUTE_PATHS.COMMERCIAL_AIRLINE_DATA_SOURCE,
    ROUTE_PATHS.COMMERCIAL_AIRLINE_CONCESSION_FEE,
    ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE,
    ROUTE_PATHS.COMMERCIAL_AIRLINE_ACCOUNT_MAINTENANCE,
    ROUTE_PATHS.COMMERCIAL_AIRLINE_VIEW_SPECIAL_ONBOARDING,
  ],
};

export const isRestrictedRouteForRole = (userRole, currentPath) => {
  const restrictedPrefixes = RESTRICTED_ROUTE_PREFIXES_BY_ROLE[userRole] || [];
  return restrictedPrefixes.some(
    (prefix) => currentPath === prefix || currentPath.startsWith(`${prefix}/`)
  );
};

export const formatDateToISOString = (date) => {
  if (!date) return null;
  return new Date(date).toISOString();
};

export const validateEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

export const getAirportData = (data, id) => {
  const airportData = data?.find((airport) => airport?.id === id);
  if (!airportData) {
    return airports.find((airport) => airport?.id === id) || null;
  }
  return airportData;
};

export const formatToTwoDecimal = (number) => {
  if (typeof number !== "number") {
    return "0.00";
  }
  return number.toFixed(2);
};

export const formatToPositiveValue = (number) => {
  if (typeof number !== "number") {
    return "0.00";
  }
  const positiveValue = Math.abs(number);
  return positiveValue.toFixed(2);
};

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatNumber = (value) => {
  return value !== undefined && value !== null && !isNaN(value)
    ? Number(value).toFixed(2)
    : "0.00";
};

export const handleEmptyValue = (item) => {
  if (item && item !== "") {
    return item;
  }
  return "---";
};

export const getFlightNumber = (item) => {
  if (item?.iataFlightNumber) {
    return item?.iataFlightNumber;
  } else if (item?.icaoFlightNumber) {
    return item?.icaoFlightNumber;
  } else {
    return item?.flightNumber;
  }
};

export const getCommercialFlightPlanId = (flightPlan) => {
  return (
    flightPlan?.id ??
    flightPlan?.cfpId ??
    flightPlan?.commercialFlightPlanId ??
    flightPlan?.flightPlanId ??
    flightPlan?.cfp?.id ??
    null
  );
};

export const normalizeCommercialFlightPlan = (flightPlan) => {
  if (!flightPlan) {
    return flightPlan;
  }

  const { cfp, ...rest } = flightPlan;
  const normalizedFlightPlan = cfp ? { ...rest, ...cfp } : { ...flightPlan };
  const resolvedFlightPlanId = getCommercialFlightPlanId(normalizedFlightPlan);

  if (resolvedFlightPlanId !== null) {
    normalizedFlightPlan.id = resolvedFlightPlanId;
  }

  return normalizedFlightPlan;
};

export const setHistoricalFlightPlanData = (flightPlan) => {
  const normalizedFlightPlan = normalizeCommercialFlightPlan(flightPlan);
  localStorage.setItem(
    HISTORICAL_DATA_STORAGE_KEY,
    JSON.stringify(normalizedFlightPlan)
  );
  return normalizedFlightPlan;
};

export const getHistoricalFlightPlanData = () => {
  const storedData = localStorage.getItem(HISTORICAL_DATA_STORAGE_KEY);
  if (!storedData) {
    return null;
  }

  try {
    return normalizeCommercialFlightPlan(JSON.parse(storedData));
  } catch {
    return null;
  }
};

export const getAirlineName = (airlineData, airlineId) => {
  return airlineData?.data?.find((airline) => airline?.id === airlineId)
    ?.airlineName;
};

export const getAirportCode = (airport) => {
  const iataCode = airport?.iataCode || airport?.iata;
  const icaoCode = airport?.icaoCode || airport?.icao;

  if (iataCode && icaoCode) {
    return `${iataCode} (${icaoCode})`;
  } else if (iataCode) {
    return iataCode;
  } else if (icaoCode) {
    return icaoCode;
  } else {
    return "Unknown";
  }
};

export const getFlightDelayTime = (delayTime, flightTime) => {
  if (!delayTime || delayTime === "00:00:00") return null;

  const parsedDelayTime = dayjs(delayTime, "HH:mm:ss");

  // Check if the dayjs object is valid
  if (!parsedDelayTime.isValid()) {
    return null;
  }

  const hours = parsedDelayTime.hour();
  const minutes = parsedDelayTime.minute();

  // Check for NaN values
  if (isNaN(hours) || isNaN(minutes)) {
    return null;
  }

  // Determine if the flight time is in the past or future
  const currentTime = dayjs();
  const flightDateTime = dayjs(flightTime);
  const isPast = flightDateTime.isBefore(currentTime);

  if (isPast) {
    return hours > 0
      ? `Flight was ${hours} h ${minutes} m delayed`
      : `Flight was ${minutes} m delayed`;
  } else {
    return hours > 0
      ? `Flight is ${hours} h ${minutes} m delay`
      : `Flight is ${minutes} m delay`;
  }
};

export const isDepartureAirportDomestic = (airportData, departureAirportId, AirportType) => {
  if (!departureAirportId || !airportData) {
    return false;
  }

  const departureAirport = getAirportData(airportData, departureAirportId);
  return departureAirport?.airportType === AirportType.DOMESTIC;
};
